/**
 * Rainbow Mulch — Playground Project Builder
 * THE CALCULATOR ENGINE (Sprint 4)
 *
 * Replaces PBMocks.derive(). Same signature, same return shape, so no template
 * and no UI code changed to adopt it — the seam pb-mocks.js established is
 * simply filled in with real arithmetic.
 *
 * ============================================================================
 * DESIGN
 * ============================================================================
 * Six pure functions, each implementing exactly one business rule, plus derive()
 * which assembles the payload the UI consumes. Pure means: numbers in, numbers
 * out, no DOM, no state mutation, no config reads outside PBConfig.
 *
 *   area(state)                  -> squareFeet         BR-05 / BR-05a
 *   volume(squareFeet, depthIn)  -> rawCubicFeet       BR-06
 *   billedVolume(raw)            -> ceil'd             BR-20 / D-021
 *   packagingUnits(billed, pack) -> units + weight     BR-10 / BR-11 / BR-14
 *   materialCost(billed, pack)   -> dollars            BR-13 / D-021
 *   comparison(billed)           -> both options       BR-09
 *
 * THREE THINGS ARE ENFORCED BY STRUCTURE, NOT BY COMMENT:
 *
 * 1. COLOUR IS NOT A PARAMETER OF ANY QUANTITY FUNCTION.
 *    D-016 says rounding happens once on total volume, never per colour. Rather
 *    than trusting a developer to remember that, the functions simply cannot
 *    see colours. Per-colour rounding is unwriteable here.
 *
 * 2. SHAPE APPEARS ONLY IN area().
 *    BR-05b. Everything downstream consumes squareFeet and cannot know whether
 *    it came from a rectangle or a circle.
 *
 * 3. THREE DISTINCT QUANTITIES, NEVER CONFLATED (D-021 as amended):
 *      raw     = squareFeet x (depth / 12)      internal intermediate
 *      billed  = ceil(raw)                      price AND cart quantity
 *      units   = ceil(billed / capacity)        freight weight, fulfilment
 *
 * ============================================================================
 * WHAT IS REAL HERE, AND WHAT IS NOT
 * ============================================================================
 * FREIGHT IS REAL as of Sprint 9 (D-043). The amount arrives on state.freight
 * from the SERVER via PBFreightClient, and this file only formats it — it must
 * never compute or default one (SH-18). Transit days likewise come from the
 * carrier's response. The fallback RANGE is real too, fitted in D-042.
 *
 * TAX is still Sprint 11 (Shopify Tax, D-002), and currently suppressed
 * entirely by D-038 rather than estimated.
 *
 * Lead time is NOT mocked: it is a settled flat constant (D-012).
 */

window.PBCalculator = (function () {
  'use strict';

  /* =====================================================================
   * PRECISION GUARD — this one is load-bearing, not defensive noise.
   *
   * `squareFeet * (depth / 12)` produces values microscopically above an
   * integer for many ordinary sizes, because depth/12 is not binary-exact for
   * most depths. A bare Math.ceil() then overcharges by a whole cubic foot.
   *
   * Real example found while building this:
   *   2 ft x 54 ft x 7"  ->  54*2*(7/12) = 63.00000000000001  ->  ceil 64
   *   the true answer is exactly 63. That is a silent $61.75 overcharge.
   *
   * A brute-force sweep of ~198,000 size combinations found 250 such cases.
   *
   * TWO independent mitigations, because this must never regress:
   *   1. Multiply before dividing — (squareFeet * depth) / 12 keeps the
   *      integers together and eliminates the error at source.
   *   2. Round to 6 decimal places before ceiling — catches anything the
   *      reordering misses (circles, fractional inputs, future formulas).
   *
   * Verified: 0 mismatches against exact arithmetic across the same sweep.
   * 6 dp is a millionth of a cubic foot; no real order is near that precision.
   * =================================================================== */
  var PRECISION = 1e6;

  function ceilVolume(x) {
    if (x === null || x === undefined || !isFinite(x)) return null;
    return Math.ceil(Math.round(x * PRECISION) / PRECISION);
  }

  /**
   * BR-21 as amended (D-037) — round money to cents AT THE POINT OF PRESENTATION,
   * and derive the total from the ROUNDED parts.
   *
   * BR-21 originally said intermediate values keep full precision and only the
   * final figure is rounded. On an itemised summary that rule causes a real
   * defect: each line rounds independently while the total rounds from
   * unrounded inputs, so the column does not add up. A sweep of 864 order
   * combinations found 38 where displayed material + freight + tax was a cent
   * away from the displayed total.
   *
   * That matters more here than a cent normally would. Objective 8 and BR-03
   * exist so the customer can CHECK the arithmetic; a column that does not sum
   * destroys exactly the trust the itemisation was built to create.
   *
   * Rounding tax on the rounded subtotal is also more correct, not less: tax is
   * owed on the amount actually charged, and the charged amount is the rounded one.
   */
  function money(x) {
    if (x === null || x === undefined || !isFinite(x)) return null;
    return Math.round(x * 100) / 100;
  }

  /** Guard for any value that must be a usable positive dimension. */
  function positive(v) {
    return typeof v === 'number' && isFinite(v) && v > 0 ? v : null;
  }

  /* =====================================================================
   * BR-05 / BR-05a — SQUARE FEET
   * The only function in the engine that knows shape exists (BR-05b).
   * =================================================================== */
  function area(state) {
    var cfg = window.PBConfig;
    var shape = state.shape || cfg.dimensions.defaultShape;

    if (shape === 'circle') {
      // BR-05a — square_feet = PI x (diameter / 2)^2
      var d = positive(state.diameter);
      if (d === null) return null;
      return Math.PI * Math.pow(d / 2, 2);
    }

    // BR-05 — square_feet = length x width
    var l = positive(state.length);
    var w = positive(state.width);
    if (l === null || w === null) return null;
    return l * w;
  }

  /* =====================================================================
   * BR-06 — RAW CUBIC FEET
   * Written in terms of squareFeet so it holds for BOTH shapes.
   * Note the multiplication order — see the PRECISION GUARD above.
   * =================================================================== */
  function volume(squareFeet, depthInches) {
    var sq = positive(squareFeet);
    var depth = positive(depthInches);
    if (sq === null || depth === null) return null;
    return (sq * depth) / 12; // NOT sq * (depth / 12)
  }

  /* =====================================================================
   * BR-20 / D-021 as amended — BILLED VOLUME
   * Billed as ordered, rounded UP to the next whole cubic foot.
   *
   * The round-up exists because Shopify cart quantities are integers, and
   * because we never leave a customer short. It is NOT a settling allowance —
   * D-029 is explicit that no overage is applied.
   * =================================================================== */
  function billedVolume(rawCubicFeet) {
    return ceilVolume(rawCubicFeet);
  }

  /* =====================================================================
   * BR-10 / BR-11 / BR-14 — PACKAGING UNITS AND WEIGHT
   *
   * One formula serves both packagings, driven entirely by config:
   *   bags  -> ceil(billed / 1)  = billed        (1 bag = 1 cu ft, OQ-20)
   *   totes -> ceil(billed / 40)
   *
   * Deriving unit count from BILLED rather than raw is deliberate and safe:
   * for any positive integer n, ceil(ceil(x)/n) === ceil(x/n). Using billed
   * keeps a single upstream source of truth instead of two parallel chains.
   * The test suite asserts that identity so it cannot silently break.
   * =================================================================== */
  function packagingUnits(billedCubicFeet, pack) {
    if (billedCubicFeet === null || !pack) return { unitCount: null, weightLbs: null };

    var cfg = window.PBConfig;
    var unitCount = Math.ceil(billedCubicFeet / pack.cubicFeetPerUnit);

    /**
     * BR-14 as amended — weight SCALES WITH ACTUAL MATERIAL (D-032, resolving OQ-10).
     *
     *   weight = billedCubicFeet x materialDensity + unitCount x tarePerUnit
     *
     * This replaced `unitCount x fullUnitWeightLbs`, which assumed every tote
     * ships FULL. It does not: totes are filled to the ordered volume, which is
     * what as-ordered billing (D-021) implies. The old form over-stated weight
     * on nearly every partial-tote order — 650 lbs for a 1 cu ft order, and
     * 650 vs the bags' 400 for the same 25 cu ft of material.
     *
     * It reduces correctly for both packagings:
     *   bags  -> billed x 16 + count x 0   = billed x 16   (unchanged)
     *   totes -> billed x 16 + count x 10
     *
     * Do NOT use pack.fullUnitWeightLbs here. That is the weight of a FULL unit,
     * kept for reference and display only.
     */
    var weightLbs =
      billedCubicFeet * cfg.material.lbsPerCubicFoot + unitCount * pack.tareLbsPerUnit;

    /**
     * Weight of the HEAVIEST single unit — what actually matters for unloading.
     *
     * With partial fills, an average per-unit weight would be fiction: a 108 cu ft
     * order is two full totes plus one part-filled, not three at 586 lbs each.
     * So: if more than one unit, at least one is full; otherwise it is the single
     * partial unit. This is the number the D-022 acknowledgement should quote.
     */
    var heaviestUnitLbs = unitCount > 1 ? pack.fullUnitWeightLbs : weightLbs;

    return { unitCount: unitCount, weightLbs: weightLbs, heaviestUnitLbs: heaviestUnitLbs };
  }

  /* =====================================================================
   * BR-13 / D-021 — MATERIAL COST
   * Billed volume x price per cubic foot. NEVER units x capacity x price —
   * that was the rejected full-tote billing model.
   * =================================================================== */
  function materialCost(billedCubicFeet, pack) {
    if (billedCubicFeet === null || !pack) return null;
    return money(billedCubicFeet * pack.pricePerCubicFoot);
  }

  /* =====================================================================
   * BR-09 — SIDE-BY-SIDE COMPARISON
   * Step 2 always shows both options, whichever is selected. Always returns
   * the full object shape (with nulls when volume is unknown) because the UI
   * reads into it unconditionally.
   * =================================================================== */
  function comparison(billedCubicFeet) {
    var cfg = window.PBConfig;
    var out = {};

    ['bags', 'tote'].forEach(function (id) {
      var pack = cfg.packaging[id];
      var u = packagingUnits(billedCubicFeet, pack);
      out[id] = {
        count: u.unitCount,
        cost: materialCost(billedCubicFeet, pack),
        weightLbs: u.weightLbs,
        heaviestUnitLbs: u.heaviestUnitLbs
      };
    });

    // Positive under as-ordered billing (D-021). Would be negative under the
    // rejected full-tote model, which is precisely why that model was rejected.
    out.tote.savingVsBags =
      out.bags.cost !== null && out.tote.cost !== null
        ? out.bags.cost - out.tote.cost
        : null;

    return out;
  }

  /**
   * Item 1 (2026-09-11) — true when a shipment ships UPS at a flat fee
   * instead of live LTL freight. Pure function of billed cubic feet alone:
   * eligibility is a physical fact about order size, not about packaging,
   * ZIP, or anything else (see pb-config.js's freight.flatFee for why).
   */
  function isFlatFeeEligible(billedCubicFeet) {
    var cfg = window.PBConfig;
    var ff = cfg && cfg.freight && cfg.freight.flatFee;
    return Boolean(ff && ff.enabled) &&
      typeof billedCubicFeet === 'number' && isFinite(billedCubicFeet) &&
      billedCubicFeet > 0 && billedCubicFeet <= ff.maxCubicFeet;
  }

  /**
   * Item 1 — the flat UPS fee for an eligible shipment, or null when not
   * eligible or the table has no entry (should not happen for an integer in
   * [1, maxCubicFeet], but null-on-missing beats a silent wrong number).
   *
   * `billedCubicFeet` is already an integer by construction (billedVolume()
   * always ceils, BR-20/D-021), and 1 bag = 1 cu ft (D-031) — so this IS the
   * bag count the table is keyed by, with no separate conversion needed.
   */
  function flatFeeForCubicFeet(billedCubicFeet) {
    var cfg = window.PBConfig;
    var ff = cfg && cfg.freight && cfg.freight.flatFee;
    if (!isFlatFeeEligible(billedCubicFeet)) return null;
    var amount = ff.table[Math.round(billedCubicFeet)];
    return typeof amount === 'number' ? amount : null;
  }

  /**
   * D-042 — the fallback freight range for the R+L-failure path.
   *
   * Delegates to PBFreightFallback so the fitted model has exactly one home.
   * Returns null rather than a guess whenever an estimate is not defensible,
   * which is D-004 applied at the seam.
   *
   * Defensive about the module being absent: the Worker imports this calculator
   * for its pure arithmetic (see backend/src/shared-rules.js) and has no reason
   * to load a display-only estimator.
   */
  function fallbackRange(weightLbs, zip) {
    var fb = window.PBFreightFallback;
    if (!fb || typeof fb.estimateRange !== 'function') return null;
    return fb.estimateRange(weightLbs, zip);
  }

  /**
   * D-036 / Sprint 9 — turn the carrier's transit day count into display text.
   *
   * R+L returns an integer (1 for Ohio, 10 for California). Composed here so no
   * template builds the wording, and singular/plural is handled once. Returns
   * null for anything non-numeric so a missing value renders as absent rather
   * than as "null business days".
   */
  function transitLabel(days) {
    var n = typeof days === 'number' ? days : Number(days);
    if (!isFinite(n) || n <= 0) return null;
    return n === 1 ? '1 business day' : n + ' business days';
  }

  /**
   * OQ-56/D-060 — true once a shipment's weight crosses the large-order
   * lead-time threshold. A pure function of weight alone, exposed here (not
   * duplicated in shared-rules.js) so the storefront's Step 5 display and the
   * backend's Draft Order tagging can never disagree about which orders
   * count as "large" — D-010's single-source-of-truth rule, same as every
   * other cross-boundary figure in this file.
   */
  function isLargeOrderByWeight(weightLbs) {
    var cfg = window.PBConfig;
    var t = cfg && cfg.leadTime && cfg.leadTime.largeOrder;
    return Boolean(t) && typeof weightLbs === 'number' && weightLbs > t.thresholdLbs;
  }

  /* =====================================================================
   * THE SEAM — same signature and return shape as PBMocks.derive(state)
   * =================================================================== */
  function derive(state) {
    var cfg = window.PBConfig;

    var squareFeet = area(state);
    var rawCubicFeet = volume(squareFeet, state.depth);
    var billed = billedVolume(rawCubicFeet);

    var pack = state.packaging ? cfg.packaging[state.packaging] : null;
    var units = packagingUnits(billed, pack);
    var cost = materialCost(billed, pack);

    /**
     * ---- freight: REAL (Sprint 9, D-043) ----
     *
     * The amount comes from the SERVER's quote, carried on state.freight by
     * PBFreightClient. It is never read from config or computed here — SH-18
     * requires the charged figure to originate server-side.
     *
     * Guarded on a finite positive number rather than merely on status, so a
     * malformed 200 cannot become $0.00 freight on a $6,000 order (the failure
     * mode parseRLMoney had in D-041).
     */
    var freight = null;
    if (state.freight && state.freight.status === 'quoted') {
      var quoted = typeof state.freight.amount === 'number'
        ? state.freight.amount
        : Number(state.freight.amount);
      if (isFinite(quoted) && quoted > 0) freight = money(quoted);
    }
    // D-004 — on 'estimated' the UI shows a labelled RANGE, never a single
    // invented number, so freightAmount stays null (SH-06, SH-07).

    /**
     * D-037 — every money figure is rounded to cents, and each total is derived
     * from the ROUNDED parts, so the itemised column always adds up.
     *
     * Charged on material + freight because freight is taxable (D-017).
     * The authoritative figure comes from Shopify Tax at checkout (D-002).
     *
     * ⚠️ OQ-47 — the rate is applied FLAT regardless of destination. For a
     * customer outside Ohio, where nexus is unconfirmed (OQ-30), this
     * OVER-STATES the total. Destination-awareness is Sprint 11.
     */
    freight = money(freight);
    var taxableBase = money((cost || 0) + (freight || 0));
    var haveBase = cost !== null && freight !== null;

    /**
     * D-038 (resolving OQ-47) — tax is NOT estimated while
     * `cfg.tax.showEstimate` is false.
     *
     * `tax` stays null and `total` is material + freight only. The payload flags
     * that with `totalIncludesTax: false` so no surface can present the figure as
     * a tax-inclusive total by accident — the number would be silently wrong,
     * which is the exact failure D-004 forbids for freight.
     */
    var tax = haveBase && cfg.tax.showEstimate
      ? money(taxableBase * cfg.tax.estimatedRate)
      : null;
    var total = haveBase ? money(taxableBase + (tax || 0)) : null;

    return {
      // Freight and tax are still mocked, so the payload is not fully real yet.
      isMock: true,

      /**
       * `cubicFeet` is the BILLED volume, not the raw geometry.
       *
       * The customer must see one volume number, and it has to be the one they
       * are charged for. Showing 27.5 cu ft beside "28 cu ft x $61.75" would
       * invite the obvious question and undermine the itemised pricing the
       * summary screen is built on.
       *
       * Raw and billed are also exposed separately for tests and for any future
       * UI that wants to show the geometry explicitly.
       */
      squareFeet: squareFeet,
      cubicFeet: billed,
      rawCubicFeet: rawCubicFeet,
      billedCubicFeet: billed,

      packagingLabel: pack ? pack.label : null,
      unitCount: units.unitCount,
      unitNoun: pack
        ? (units.unitCount === 1 ? pack.unitNoun : pack.unitNounPlural)
        : null,

      materialCost: cost,
      weightLbs: units.weightLbs,
      heaviestUnitLbs: units.heaviestUnitLbs,

      comparison: comparison(billed),

      freightAmount: freight,

      /**
       * D-042 (OQ-15 resolved) — a REAL fitted range, replacing the invented
       * $700–$1,000 that D-004 would never have allowed to ship.
       *
       * null when no defensible estimate exists (unrecognised ZIP, or a
       * non-LTL lane like AK/HI). Callers must render nothing in that case
       * rather than substituting a placeholder.
       */
      freightRange: fallbackRange(units.weightLbs, state.zip),
      /**
       * D-036 — transit is only knowable once the carrier has quoted, so it is
       * null until then. It previously returned the mock string unconditionally,
       * which let Step 5 claim a transit estimate before R+L had been called.
       *
       * OQ-05 resolved transit as SEPARATE from production lead time and shown
       * beside the freight quote — so it must not appear without one.
       * MOCK (Sprint 9): the real value comes from the R+L rate response.
       */
      transitDaysLabel: state.freight && state.freight.status === 'quoted'
        ? transitLabel(state.freight.transitDays)
        : null,

      // D-037 — already rounded to cents, so the displayed column always sums
      // to the displayed total. `haveBase` IS `cost !== null && freight !== null`
      // (see above); this key was duplicated in both forms until esbuild's
      // duplicate-key warning surfaced it. Behaviour was unaffected — the two
      // expressions are identical — but a future edit to the dead line would
      // have silently done nothing.
      subtotal: haveBase ? taxableBase : null,
      tax: tax,
      // D-038 — when false, `total` EXCLUDES tax and must be labelled as such.
      totalIncludesTax: cfg.tax.showEstimate,
      taxPlaceholder: cfg.tax.showEstimate ? null : cfg.tax.placeholderLabel,
      total: total,

      // Settled, NOT mocked — flat for every colour and blend (D-012).
      // D-036 — PRODUCTION time. Qualifier and composed phrase both come from
      // config so no surface builds the wording itself.
      //
      // OQ-56/D-060 — large-order OVERRIDE, not a change to D-012's flat rule:
      // below the threshold this is identical to before; above it, the label
      // swaps to the "we'll confirm" wording. The qualifier ("Ships in")
      // stays the same either way — only the duration/promise part changes.
      leadTimeLabel: isLargeOrderByWeight(units.weightLbs) ? cfg.leadTime.largeOrder.label : cfg.leadTime.label,
      leadTimeQualifier: cfg.leadTime.qualifier,
      leadTimeDisplay: cfg.leadTime.qualifier + ' ' + (isLargeOrderByWeight(units.weightLbs) ? cfg.leadTime.largeOrder.label : cfg.leadTime.label)
    };
  }

  return {
    derive: derive,
    // Exposed for the test suite and for Sprint 5/8 reuse.
    area: area,
    volume: volume,
    billedVolume: billedVolume,
    packagingUnits: packagingUnits,
    materialCost: materialCost,
    comparison: comparison,
    ceilVolume: ceilVolume,
    isLargeOrderByWeight: isLargeOrderByWeight,
    isFlatFeeEligible: isFlatFeeEligible,
    flatFeeForCubicFeet: flatFeeForCubicFeet
  };
})();
